import React, { useEffect, useState } from "react";
import { Breadcrumbs, Typography, Box } from "@mui/material";
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import TooltipText from "components/TooltipText";
import theme from "@theme/";
import { projectType } from "utils/lookUpTable";
import InnoIcons from "@InnoIcons/";
import styled from "@emotion/styled";
import { apiPost } from "api/callApi";
import { isValidFolderId } from "utils/helper";

function CustomBreadcrumb({ items = [] }) {
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const navigate = useNavigate();
  const folderId = searchParams.get("folderId");
  const projectId = searchParams.get("projectId");
  const fileId = searchParams.get("fileId");
  const [data, setData] = useState();

  const getBredcrumbsData = async () => {
    try {
      if (!isValidFolderId(folderId)) {
        setData("");
        return;
      }
      const result = await apiPost(`/v1/file/path/breadcrum/${folderId}`);
      setData(result.path);
    } catch (error) {
      console.log("Error", error);
    }
  };

  useEffect(() => {
    getBredcrumbsData();
  }, [folderId]);

  const currentProject = useSelector(
    (state) => state?.currentProject?.data || {},
  );
  const currentOccupant = useSelector(
    (state) => state?.currentOccupant?.data || {},
  );

  const isOccupantRoute = type === String(projectType.occupant);
  const isProjectLoaded = Boolean(currentProject?.propertyName);
  const isOccupantLoaded =
    !isOccupantRoute ||
    (currentOccupant?.id &&
      String(currentOccupant.id) === String(projectId) &&
      currentOccupant?.occupantName);

  const staticBreadcrumbs =
    isOccupantRoute
      ? [
          {
            label: currentProject?.propertyName,
            to: "/project-home",
            loading: !isProjectLoaded,
          },
          {
            label: "Levels",
            to: `/project-home/levels?type=${projectType?.property}`,
          },
          {
            label: currentOccupant?.levelName,
            to: `/project-home/levels?type=${projectType.property}&levelId=${currentOccupant.levelId}`,
            loading: !isOccupantLoaded,
          },
          {
            label: `${currentOccupant?.occupantName || ""} - ${
              currentOccupant?.occupantNo || ""
            }`,
            to: `/project-home/occupanthomepage?projectId=${
              currentOccupant.id
            }&type=${1}`,
            loading: !isOccupantLoaded,
          },
        ]
      : [
          {
            label: currentProject.propertyName,
            to: "/project-home",
            loading: !isProjectLoaded,
          },
        ];

  // Combine static and dynamic items
  const combinedBreadcrumbs = [...staticBreadcrumbs, ...items];

  const handleBackClick = () => {
    if (type == 0) navigate("/");
    if (type == 1)
      navigate(`/project-home/generalinformation?type=${projectType.property}`);
  };
  const handleClick = () => {
    if (fileId) {
      navigate(
        `/document?projectId=${projectId}&folderId=${folderId}&type=${type}`,
      );
    }
  };

  const ContainerStyle = styled.div`
    display: flex;
    align-items: center;

    gap: 10px;

    @media (max-width: 768px) {
      margin-bottom: ${(props) => (props.type == 1 ? "-12px" : "0")};
    }
  `;

  const BodyHeader = styled.div`
    padding: 16px 24px;
    background: linear-gradient(135deg, #e36e00 0%, #ffae00 100%);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    position: fixed;
    width: 100%;
    z-index: 1000;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  `;

  const BreadcrumbShimmer = styled.span`
    display: inline-block;
    width: 96px;
    height: 18px;
    border-radius: 10px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.22) 25%,
      rgba(255, 255, 255, 0.48) 37%,
      rgba(255, 255, 255, 0.22) 63%
    );
    background-size: 400% 100%;
    animation: breadcrumb-shimmer 1.2s ease-in-out infinite;

    @keyframes breadcrumb-shimmer {
      0% {
        background-position: 100% 0;
      }
      100% {
        background-position: 0 0;
      }
    }
  `;

  const renderBreadcrumbLabel = (item) =>
    item.loading ? (
      <BreadcrumbShimmer />
    ) : (
      <TooltipText title={item.label} maxLength={100} />
    );

  return (
    <BodyHeader>
      <Box>
        <ContainerStyle type={type}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onClick={handleBackClick}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            {type == 0 ? (
              <InnoIcons.DashboardIcon
                sx={{
                  fontSize: "20px",
                  color: "white",
                }}
              />
            ) : (
              <InnoIcons.ArrowBackIcon
                sx={{
                  fontSize: "20px",
                  color: "white",
                }}
              />
            )}
          </div>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator=">"
            sx={{
              "& .MuiBreadcrumbs-separator": {
                color: "white", // sets the separator color to white
              },
            }}
          >
            {combinedBreadcrumbs.map((item, index) => {
              const isLastItem = index === combinedBreadcrumbs.length - 1;
              return item.to ? (
                <RouterLink
                  key={index}
                  to={item.loading ? "#" : item.to}
                  onClick={(event) => {
                    if (item.loading) {
                      event.preventDefault();
                    }
                  }}
                  style={{
                    textDecoration: "none",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    backgroundColor: "#DC7F32",
                    padding: "5px 10px ",
                    borderRadius: "10px",
                  }}
                >
                  {renderBreadcrumbLabel(item)}
                </RouterLink>
              ) : (
                <Typography
                  key={index}
                  color={isLastItem ? theme.colors.themeColor : "#333333"}
                  sx={{
                    fontWeight: "bold",
                    fontSize: "13px",
                  }}
                >
                  {item.loading ? (
                    <BreadcrumbShimmer />
                  ) : (
                    <TooltipText title={item.label} maxLength={30} />
                  )}
                </Typography>
              );
            })}
            {data && (
              <span
                style={{
                  textDecoration: "none",
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "14px",
                  backgroundColor: theme.colors.themeColor,
                  padding: "5px 10px",
                  borderRadius: "10px",
                  cursor: fileId ? "pointer" : "default",
                }}
                onClick={handleClick}
              >
                <TooltipText title={data} maxLength={130} />
              </span>
            )}
          </Breadcrumbs>
        </ContainerStyle>
      </Box>
    </BodyHeader>
  );
}

export default CustomBreadcrumb;
